/**
 * Crescent Flexipack — shared layout (navbar + footer).
 * Visually matches the client's uploaded homepage mockup: green/blue brand,
 * utility bar with language + phone + email, main nav row below.
 */
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, MessageCircle } from "lucide-react";
import CrescentAssistantWidget from "./CrescentAssistantWidget";
import { LanguageSwitcher, ExitIntentModal, useCrescentLang } from "./CrescentI18n";

const PRE_BUILT_LANGS = ["en", "es", "fr", "de", "pt", "ar", "hi", "zh"];

const NAV = [
  { label: "Home",         to: "/crescent" },
  { label: "About Us",     to: "/crescent/about" },
  { label: "Products",     to: "/crescent/products" },
  { label: "Industries",   to: "/crescent/industries" },
  { label: "Sustainability", to: "/crescent/sustainability" },
  { label: "Quality",      to: "/crescent/quality" },
  { label: "Our Capabilities", to: "/crescent/capabilities" },
  { label: "Resources",    to: "/crescent/resources" },
  { label: "Contact",      to: "/crescent/contact" },
];

export function CrescentNavbar() {
  const { pathname } = useLocation();
  return (
    <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-4 gap-4">
          <Link to="/crescent" className="flex items-center gap-2 shrink-0" data-testid="crescent-logo">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white font-black text-lg shadow-sm">C</div>
            <div>
              <div className="font-heading font-extrabold text-lg leading-none text-slate-900">
                CRESCENT <span className="text-emerald-700">FLEXI PACK</span>
              </div>
              <div className="text-[9px] font-medium text-slate-500 tracking-widest leading-tight">
                Flexible Packaging for a Better Tomorrow
              </div>
            </div>
          </Link>
          <div className="hidden xl:flex items-center gap-5 text-xs text-slate-500">
            <LanguageSwitcher />
            <span className="inline-flex items-center gap-1.5"><Phone size={13} /> +91 484 255 0000</span>
            <span className="inline-flex items-center gap-1.5"><Mail size={13} /> sales@crescentflexipack.com</span>
          </div>
          <Link
            to="/crescent/request-quote"
            data-testid="crescent-nav-quote"
            className="hidden sm:inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-full text-sm shrink-0 transition-colors"
          >
            Request a Quote
          </Link>
        </div>
        <nav className="hidden lg:flex items-center gap-6 pb-3">
          {NAV.map((item) => {
            const active = pathname === item.to || (item.to !== "/crescent" && pathname.startsWith(item.to));
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`crescent-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-sm font-semibold transition-colors ${active ? "text-emerald-700 border-b-2 border-emerald-600 pb-2" : "text-slate-700 hover:text-emerald-700"}`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
      </div>
    </header>
  );
}

export function CrescentFooter() {
  return (
    <footer className="bg-slate-900 text-slate-300 pt-14 pb-8 mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white font-black">C</div>
            <div className="font-heading font-extrabold text-white">CRESCENT FLEXI PACK</div>
          </div>
          <p className="text-xs text-slate-400 mt-3 leading-relaxed">
            Flexible Packaging for a Better Tomorrow. Manufactured in Kerala, India for Indian and international brands.
          </p>
        </div>
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-white mb-3">Products</h3>
          <ul className="space-y-1.5 text-sm">
            <li><Link to="/crescent/products/stand-up-pouches" className="hover:text-white">Stand-Up Pouches</Link></li>
            <li><Link to="/crescent/products/printed-laminated-rolls" className="hover:text-white">Printed Laminated Rolls</Link></li>
            <li><Link to="/crescent/products/zipper-pouches" className="hover:text-white">Zipper Pouches</Link></li>
            <li><Link to="/crescent/products/center-seal-pouches" className="hover:text-white">Center-Seal Pouches</Link></li>
            <li><Link to="/crescent/products" className="hover:text-white font-semibold text-emerald-400">All products →</Link></li>
          </ul>
        </div>
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-white mb-3">Industries</h3>
          <ul className="space-y-1.5 text-sm">
            <li><Link to="/crescent/industries/seafood-frozen-food" className="hover:text-white">Seafood & Frozen Food</Link></li>
            <li><Link to="/crescent/industries/pet-food" className="hover:text-white">Pet Food</Link></li>
            <li><Link to="/crescent/industries/tea-coffee-spices" className="hover:text-white">Tea, Coffee & Spices</Link></li>
            <li><Link to="/crescent/industries/private-label" className="hover:text-white">Private Label</Link></li>
            <li><Link to="/crescent/industries" className="hover:text-white font-semibold text-emerald-400">All industries →</Link></li>
          </ul>
        </div>
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-white mb-3">Contact</h3>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2"><Phone size={14} className="mt-0.5" /> +91 484 255 0000</li>
            <li className="flex items-start gap-2"><Mail  size={14} className="mt-0.5" /> sales@crescentflexipack.com</li>
            <li className="flex items-start gap-2"><MessageCircle size={14} className="mt-0.5" /> WhatsApp available</li>
            <li className="text-xs text-slate-500 pt-2">Alappuzha, Kerala, India</li>
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-t border-slate-800 mt-10 pt-6 text-xs text-slate-500 flex flex-wrap justify-between gap-2">
        <span>© {new Date().getFullYear()} Crescent Flexi Pack. All rights reserved.</span>
        <span>Printing · Packaging · A Sustainable Tomorrow</span>
      </div>
    </footer>
  );
}

function HreflangTags() {
  const { pathname } = useLocation();
  useEffect(() => {
    const existing = document.head.querySelectorAll("link[rel='alternate'][data-crescent-hreflang]");
    existing.forEach((n) => n.parentNode.removeChild(n));
    const origin = window.location.origin;
    PRE_BUILT_LANGS.forEach((code) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", code);
      link.setAttribute("href", `${origin}${pathname}?lang=${code}`);
      link.setAttribute("data-crescent-hreflang", "1");
      document.head.appendChild(link);
    });
    const xdef = document.createElement("link");
    xdef.setAttribute("rel", "alternate");
    xdef.setAttribute("hreflang", "x-default");
    xdef.setAttribute("href", `${origin}${pathname}`);
    xdef.setAttribute("data-crescent-hreflang", "1");
    document.head.appendChild(xdef);
    return () => {
      document.head.querySelectorAll("link[rel='alternate'][data-crescent-hreflang]").forEach((n) => n.parentNode.removeChild(n));
    };
  }, [pathname]);
  return null;
}

function LayoutShell({ children }) {
  const { activeMeta } = useCrescentLang();
  return (
    <div className="min-h-screen bg-white" dir={activeMeta.rtl ? "rtl" : "ltr"}>
      <HreflangTags />
      <CrescentNavbar />
      <main className="min-h-[60vh]">{children}</main>
      <CrescentFooter />
      <CrescentAssistantWidget />
      <ExitIntentModal />
    </div>
  );
}

export default function CrescentLayout({ children }) {
  return <LayoutShell>{children}</LayoutShell>;
}
